import moment from '@e3dc-react/shell/Libs/moment'
import { ChartView } from 'src/Libs/chart-kit/shared/utils/chartModel'
import { CostSavingChartData } from '../../types'
import { UnitType, UnitTypes } from 'src/Libs/types'
import { YAxisDomainConfig } from '../../config'

/**
 * Get tick values for the x axis
 *
 * If additional data points have been added for visualization purposes then do not display a label for that tick
 *
 * @param view
 * @param from
 * @param to
 * @returns
 */
export const getXAxisTickValues = (view: ChartView, from: number, to: number, downSm: boolean, dataPointsCount: number): number[] => {
  const tickValues: number[] = []
  const startTS = moment(from)
  const stepSize = getStepSize(view, dataPointsCount, downSm)

  if (view === 'day') {
    const selectedDay = moment(from).startOf('day')
    const correctedTo = moment(to).endOf('day')
    while (selectedDay.isBefore(correctedTo)) {
      tickValues.push(selectedDay.valueOf())
      selectedDay.add(stepSize, 'hour')
    }
  }

  if (view === 'week') {
    startTS.startOf('day')
    while (startTS.isBefore(to)) {
      tickValues.push(startTS.valueOf())
      startTS.add(stepSize, 'day')
    }
  }

  if (view === 'month') {
    startTS.startOf('month')
    while (startTS.isBefore(to)) {
      tickValues.push(startTS.valueOf())
      startTS.add(stepSize, 'day')
    }
  }

  if (view === 'year') {
    startTS.startOf('year')
    while (startTS.isBefore(to)) {
      tickValues.push(startTS.valueOf())
      startTS.add(stepSize, 'month')
    }
  }

  return tickValues
}

/**
 * Determines the tick steps for the chart based on the view and the amount of data points.
 *
 * @param view
 * @param dataPointsCount
 * @param downSm
 * @returns
 */
const getStepSize = (view: ChartView, dataPointsCount: number, downSm: boolean): number => {
  if (view === 'day') {
    if (downSm) return 4
    return 3
  }
  if (view === 'week') return 1
  if (view === 'month') {
    if (downSm && dataPointsCount > 16) return 6
    return 3
  }
  if (view === 'year') {
    if (downSm && dataPointsCount > 6) return 2
    return 1
  }
  return 1
}

/**
 * Calculate the steps for the chart which must be in 15 minute intervals.
 * TODO: use this function when switching to the new optimized history-values/steps route
 *
 * @param view
 * @returns
 */
export const getSteps = (view: ChartView): number => {
  const stepUnit = 15
  const daySteps = stepUnit * 4

  if (view === 'day') return daySteps

  if (view === 'week') return daySteps * 24

  if (view === 'month') return daySteps * 24

  if (view === 'year') return daySteps * 24 * 30

  return stepUnit * 1
}

/**
 * Returns view type to use for this specific chart.
 * Necessary, because we want to show different data as set per default for each view
 *
 * @param view
 * @returns
 */
export const transformViewType = (view: ChartView): ChartView => {
  if (view === 'day') return 'week'
  if (view === 'week' || view === 'month') return 'month'
  if (view === 'year') return 'year'
  return 'week'
}

/**
 * Checks if threeSixtySavings are negative
 */
export const checkHasNegativeValuesOnLeftYAxis = (data: CostSavingChartData[]): boolean => {
  const negativePrice = data.find(item => (item.threeSixtyWallboxSavings ?? 0) < 0 || (item.threeSixtyBatterySavings ?? 0) < 0)
  return !!negativePrice
}

/**
 * customize chart ticks
 */
export const formatYTicks = (value: string, unit: UnitType): string => {
  if (unit === UnitTypes.euros) {
    const formattedValue = Number(value).toFixed(2)
    return `${formattedValue} ${unit}`
  }
  return `${value} ${unit}`
}

export const getTimePeriod = (view: ChartView): ChartView => {
  if (view === 'day') return 'hour'
  if (view === 'week' || view === 'month') return 'day'
  if (view === 'year') return 'month'
  return 'hour'
}

/**
 * Get a list of ticks with equal step size. Ensure ~20% buffer in both directions with a minimum buffer of 1.
 */
export const getTicksWithEqualStepSize = (minValue: number, maxValue: number, tickCount: number): number[] => {
  if (tickCount < 2) {
    throw new Error('tickCount must be at least 2')
  }

  const range = maxValue - minValue
  const buffer = Math.max(Math.round(range * YAxisDomainConfig.BoundaryFactor), YAxisDomainConfig.BoundaryMinimum)

  const adjustedMin = Math.floor(minValue - buffer)
  const adjustedMax = Math.ceil(maxValue + buffer)
  const adjustedRange = adjustedMax - adjustedMin

  const stepSize = Math.max(1, Math.round(adjustedRange / (tickCount - 1)))
  const ticks: number[] = []

  for (let i = 0; i < tickCount; i++) {
    ticks.push(adjustedMin + i * stepSize)
  }

  return ticks
}
